import { Accessor, createSignal, onCleanup, onMount } from 'solid-js';

export interface ProjectDataSelectionModifiers {
  isAdditive: Accessor<boolean>;
  isRange: Accessor<boolean>;
  syncModifier(e: {
    ctrlKey: boolean;
    metaKey: boolean;
    shiftKey: boolean;
  }): void;
  clearModifier(): void;
}

/**
 * Tracks cmd/ctrl (additive) and shift (range) so panel-wide selection can
 * merge across trees. Hosts that own selection should create it with these
 * accessors and pass the same object into the panel for mouse sync.
 */
export function useProjectDataSelectionModifiers(): ProjectDataSelectionModifiers {
  const [isAdditive, setIsAdditive] = createSignal(false);
  const [isRange, setIsRange] = createSignal(false);

  const syncModifier = (e: {
    ctrlKey: boolean;
    metaKey: boolean;
    shiftKey: boolean;
  }): void => {
    setIsAdditive(e.ctrlKey || e.metaKey);
    setIsRange(e.shiftKey);
  };

  const clearModifier = (): void => {
    setIsAdditive(false);
    setIsRange(false);
  };

  onMount(() => {
    document.addEventListener('keydown', syncModifier);
    document.addEventListener('keyup', syncModifier);
    // A held key whose keyup we miss (e.g. window switch) must not stick.
    window.addEventListener('blur', clearModifier);
  });
  onCleanup(() => {
    document.removeEventListener('keydown', syncModifier);
    document.removeEventListener('keyup', syncModifier);
    window.removeEventListener('blur', clearModifier);
  });

  return {
    isAdditive,
    isRange,
    syncModifier,
    clearModifier,
  };
}
